package com.evoapps.evoshift.ui.adaptive

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.BoxWithConstraintsScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

/**
 * Material 3 window size classes by width (the breakpoints of androidx.window's
 * `WindowSizeClass.BREAKPOINTS_V2`): phones (< 600dp), tablets in portrait / small windows
 * (600-840dp), tablets in landscape (840-1200dp), laptops and desktop windows (1200-1600dp,
 * SH-98: Googlebook) and very wide windows (>= 1600dp). Provided once by the app shell; screens
 * read [LocalWindowWidthClass] instead of measuring themselves.
 *
 * The app measures its own window (a free-form desktop window is resized at any time), never the
 * display, so a half-screen laptop window gets the tablet layout and a phone's the phone one.
 */
enum class WindowWidthClass { COMPACT, MEDIUM, EXPANDED, LARGE, EXTRA_LARGE;
    val isCompact get() = this == COMPACT
    /** Expanded or wider: two panes, the rail, card grids. */
    val isExpanded get() = this >= EXPANDED
    /** Laptop-wide or wider (SH-98): docked side panes, wider content, more columns. */
    val isLarge get() = this >= LARGE

    /** How wide lists and dashboards may grow: wider on laptops so the extra space is used. */
    val wideContentMaxWidth: Dp get() = when (this) {
        LARGE -> LargeContentMaxWidth
        EXTRA_LARGE -> ExtraLargeContentMaxWidth
        else -> WideContentMaxWidth
    }

    companion object {
        fun fromWidth(width: Dp): WindowWidthClass = when {
            width < 600.dp -> COMPACT
            width < 840.dp -> MEDIUM
            width < 1200.dp -> EXPANDED
            width < 1600.dp -> LARGE
            else -> EXTRA_LARGE
        }
    }
}

val LocalWindowWidthClass = compositionLocalOf { WindowWidthClass.COMPACT }

/**
 * SH-105: Material 3 window size classes by height: compact (< 480dp: a phone in landscape),
 * medium (480-900dp) and expanded (>= 900dp). Side-by-side panes need more than a compact height.
 */
enum class WindowHeightClass { COMPACT, MEDIUM, EXPANDED;
    val isCompact get() = this == COMPACT

    companion object {
        fun fromHeight(height: Dp): WindowHeightClass = when {
            height < 480.dp -> COMPACT
            height < 900.dp -> MEDIUM
            else -> EXPANDED
        }
    }
}

val LocalWindowHeightClass = compositionLocalOf { WindowHeightClass.MEDIUM }

/**
 * SH-105: the width class a [width] x [height] window lays out for in [posture]. A foldable opened
 * flat with a nearly square inner display (iPhone Duo, 669 x 951 pt in portrait) counts as
 * expanded although it is only medium wide: it is one tablet-like page, so screens show their
 * list and detail side by side there, as on a tablet in landscape.
 */
fun windowWidthClassFor(width: Dp, height: Dp, posture: DevicePosture = DevicePosture.Normal): WindowWidthClass {
    val byWidth = WindowWidthClass.fromWidth(width)
    val flatSquare = posture == DevicePosture.Flat && postureLayoutFor(posture, width, height) == PostureLayout.TWO_PANE
    return if (byWidth == WindowWidthClass.MEDIUM && flatSquare) WindowWidthClass.EXPANDED else byWidth
}

/**
 * Measures the available width and provides [LocalWindowWidthClass] to [content] (the window's,
 * with the device's posture; a detail pane passes [DevicePosture.Normal] for its own width).
 */
@Composable
fun ProvideWindowWidthClass(
    modifier: Modifier = Modifier,
    posture: DevicePosture = LocalDevicePosture.current,
    content: @Composable BoxScope.(WindowWidthClass) -> Unit
) {
    BoxWithConstraints(modifier) {
        val widthClass = windowWidthClassFor(maxWidth, maxHeight, posture)
        // A pane measured inside a scrolling column has no height of its own: the window's then.
        val heightClass = if (maxHeight == Dp.Infinity) LocalWindowHeightClass.current else WindowHeightClass.fromHeight(maxHeight)
        CompositionLocalProvider(LocalWindowWidthClass provides widthClass, LocalWindowHeightClass provides heightClass) {
            content(widthClass)
        }
    }
}

/** Reading width for forms and text-heavy screens on wide windows. */
val ContentMaxWidth = 640.dp

/** Short forms such as sign-in: wider looks empty on a desktop. */
val FormMaxWidth = 480.dp

/** Card lists (team, requests, rosters): wider than a form, still readable. */
val ListMaxWidth = 840.dp

/** Dashboards: wider, still not edge to edge on a desktop monitor. */
val WideContentMaxWidth = 1100.dp

/** Lists and dashboards in a laptop-wide window (SH-98): a fourth card column fits. */
val LargeContentMaxWidth = 1440.dp

/** Lists and dashboards in a very wide window (SH-98). */
val ExtraLargeContentMaxWidth = 1760.dp

/**
 * Caps content's width on tablets and desktops and centres it; a no-op on phones where the window
 * is narrower than [maxWidth].
 *
 * SH-130: never on a scrolling container itself, or the mouse wheel and the trackpad scroll only
 * over the centred column and not over the empty sides. The container fills the screen and this
 * goes after its scroll: `fillMaxSize().verticalScroll(state).contentWidth(max)`. Lazy lists and
 * grids get the side margin in their content padding instead: [CentredLazyColumn],
 * [AdaptiveCardGrid]'s `maxWidth`, or [CentredBox] for anything else that scrolls.
 */
fun Modifier.contentWidth(maxWidth: Dp = ContentMaxWidth): Modifier =
    this.wrapContentWidth(Alignment.CenterHorizontally).widthIn(max = maxWidth)

/** SH-130: the side margin that centres a column of at most [maxWidth] in [available] width. */
fun centringMargin(available: Dp, maxWidth: Dp): Dp =
    if (available == Dp.Infinity) 0.dp else ((available - maxWidth) / 2).coerceAtLeast(0.dp)

/** This padding with [margin] more on the left and the right. */
fun PaddingValues.plusHorizontal(margin: Dp): PaddingValues =
    if (margin == 0.dp) this else HorizontallyWidened(this, margin)

private class HorizontallyWidened(private val base: PaddingValues, private val margin: Dp) : PaddingValues {
    override fun calculateLeftPadding(layoutDirection: LayoutDirection): Dp = base.calculateLeftPadding(layoutDirection) + margin
    override fun calculateTopPadding(): Dp = base.calculateTopPadding()
    override fun calculateRightPadding(layoutDirection: LayoutDirection): Dp = base.calculateRightPadding(layoutDirection) + margin
    override fun calculateBottomPadding(): Dp = base.calculateBottomPadding()
    override fun equals(other: Any?): Boolean = other is HorizontallyWidened && other.base == base && other.margin == margin
    override fun hashCode(): Int = base.hashCode() * 31 + margin.hashCode()
}

/**
 * SH-130: a full-size box that hands [content] the side margin centring a column of at most
 * [maxWidth]. For a scrolling container that cannot take [contentWidth] after its scroll (a lazy
 * list or grid, a pager): it fills the box and adds the margin to its content padding, so the
 * wheel scrolls it from the empty sides too.
 */
@Composable
fun CentredBox(
    maxWidth: Dp,
    modifier: Modifier = Modifier,
    content: @Composable BoxWithConstraintsScope.(margin: Dp) -> Unit
) {
    BoxWithConstraints(modifier.fillMaxSize()) {
        content(centringMargin(this.maxWidth, maxWidth))
    }
}

/**
 * A LazyColumn across the whole width with its items in a centred column of at most [maxWidth]
 * (SH-130): the side margins belong to the list, so the wheel and the trackpad scroll it there as
 * well. [modifier] is the list's own (test tags, semantics); it fills the size it gets.
 */
@Composable
fun CentredLazyColumn(
    maxWidth: Dp,
    modifier: Modifier = Modifier,
    state: LazyListState = rememberLazyListState(),
    contentPadding: PaddingValues = PaddingValues(0.dp),
    verticalArrangement: Arrangement.Vertical = Arrangement.Top,
    horizontalAlignment: Alignment.Horizontal = Alignment.Start,
    content: LazyListScope.() -> Unit
) {
    BoxWithConstraints(Modifier.fillMaxSize()) {
        LazyColumn(
            modifier = modifier.fillMaxSize(),
            state = state,
            contentPadding = contentPadding.plusHorizontal(centringMargin(this.maxWidth, maxWidth)),
            verticalArrangement = verticalArrangement,
            horizontalAlignment = horizontalAlignment,
            content = content
        )
    }
}

/**
 * Centres [content] and caps its width on tablets and desktops, so forms and lists do not stretch
 * across a whole monitor; on phones it is the full width.
 */
@Composable
fun ContentColumn(
    modifier: Modifier = Modifier,
    maxWidth: Dp = ContentMaxWidth,
    content: @Composable BoxScope.() -> Unit
) {
    Box(modifier = modifier.fillMaxWidth(), contentAlignment = Alignment.TopCenter) {
        Box(modifier = Modifier.widthIn(max = maxWidth).fillMaxWidth(), content = content)
    }
}
